import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import { useLazyRefreshUserProfileQuery, useUpdateUserMutation } from '../slices/usersApiSlice';
import { useGetGarminAuthUrlMutation, useGetGarminStatusQuery, useDisconnectGarminMutation, useLinkGarminFromRecentPushMutation } from '../slices/garminApiSlice';
import { useDisconnectStravaMutation, useGetStravaAuthUrlMutation } from '../slices/stravaApiSlice';
import { useFetchAthleteByUserIdQuery, useUpdateAthleteMutation } from '../slices/athleteApiSlice';
import { setCredentials } from '../slices/authSlice';
import {
  useCreateBillingCheckoutSessionMutation,
  useCreateBillingPortalSessionMutation,
} from '../slices/billingApiSlice';

const ProfileScreen = () => {
  const [email, setEmail] = useState('');
  const [username, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [userLeague, setUserLeague] = useState(null);
  const [userTeam, setUserTeam] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth);

  const [updateProfile, { isLoading }] = useUpdateUserMutation();
  const [refreshUserProfile] = useLazyRefreshUserProfileQuery();
  const [createBillingCheckoutSession, { isLoading: isCreatingCheckout }] = useCreateBillingCheckoutSessionMutation();
  const [createBillingPortalSession, { isLoading: isCreatingPortal }] = useCreateBillingPortalSessionMutation();
  const [getStravaAuthUrl] = useGetStravaAuthUrlMutation();
  const [disconnectStrava, { isLoading: isDisconnectingStrava }] = useDisconnectStravaMutation();
  const { data: garminStatus, refetch: refetchGarmin } = useGetGarminStatusQuery(undefined, { skip: !userInfo });
  const [getGarminAuthUrl] = useGetGarminAuthUrlMutation();
  const [disconnectGarmin, { isLoading: isDisconnecting }] = useDisconnectGarminMutation();
  const [linkGarminFromRecentPush, { isLoading: isLinkingGarmin }] = useLinkGarminFromRecentPushMutation();
  const { data: athlete, isLoading: isLoadingAthlete } = useFetchAthleteByUserIdQuery(userInfo?._id, { skip: !userInfo });
  const [updateAthlete, { isLoading: isUpdatingAthlete }] = useUpdateAthleteMutation();

  useEffect(() => {
    setName(userInfo.username);
    setEmail(userInfo.email);

    // Set league and team info if available
    if (userInfo.league) {
      setUserLeague(userInfo.league);
    }
    if (userInfo.team) {
      setUserTeam(userInfo.team);
    }
  }, [userInfo.email, userInfo.username, userInfo.league, userInfo.team]);

  // Populate athlete name fields when athlete data is loaded
  useEffect(() => {
    if (athlete) {
      setFirstName(athlete.firstName || '');
      setLastName(athlete.lastName || '');
    }
  }, [athlete]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const billingState = searchParams.get('billing');

    if (!billingState || !userInfo) {
      return;
    }

    const syncBillingState = async () => {
      if (billingState === 'success' || billingState === 'portal') {
        try {
          const refreshedUserInfo = await refreshUserProfile().unwrap();
          dispatch(setCredentials({ userInfo: refreshedUserInfo }));
        } catch (error) {
          toast.error(error?.data?.message || 'Failed to refresh billing status');
        }
      }

      if (billingState === 'success') {
        toast.success('Billing update received. Your account status has been refreshed.');
      }

      if (billingState === 'cancelled') {
        toast.info('Stripe checkout was cancelled. No billing changes were made.');
      }

      navigate('/profile', { replace: true });
    };

    void syncBillingState();
  }, [dispatch, location.search, navigate, refreshUserProfile, userInfo]);

  const openGarminConnect = async () => {
    try {
      const { authUrl } = await getGarminAuthUrl().unwrap();
      const w = window.open(authUrl, 'garmin_oauth', 'width=600,height=700');

      if (!w || w.closed || typeof w.closed === 'undefined') {
        toast.info("Your browser blocked the Garmin popup. We're opening Garmin in this tab instead.");
        window.location.assign(authUrl);
        return;
      }

      w.focus?.();
      const start = Date.now();
      const timer = setInterval(async () => {
        const result = await refetchGarmin();
        const connected = result?.data?.connected || garminStatus?.connected;
        if (connected || Date.now() - start > 120000 || (w && w.closed)) {
          clearInterval(timer);
        }
      }, 2000);
    } catch (e) {
      console.error('Failed to start Garmin connect', e);
      toast.error(e?.data?.message || 'Failed to start Garmin connection');
    }
  };

  const handleConnectStrava = async () => {
    try {
      const { authUrl } = await getStravaAuthUrl().unwrap();
      window.location.assign(authUrl);
    } catch (e) {
      console.error('Failed to start Strava connect', e);
      toast.error(e?.data?.message || 'Failed to start Strava connection');
    }
  };

  const handleDisconnectStrava = async () => {
    try {
      await disconnectStrava().unwrap();
      const refreshedUserInfo = await refreshUserProfile().unwrap();
      dispatch(setCredentials({ userInfo: refreshedUserInfo }));
      toast.success('Strava disconnected successfully');
    } catch (e) {
      console.error('Failed to disconnect Strava', e);
      toast.error(e?.data?.message || 'Failed to disconnect Strava');
    }
  };

  const handleDisconnectGarmin = async () => {
    try {
      await disconnectGarmin({ notify: true }).unwrap();
      await refetchGarmin();
      toast.success('Garmin disconnected successfully');
    } catch (e) {
      console.error('Failed to disconnect Garmin', e);
      toast.error('Failed to disconnect Garmin');
    }
  };

  const handleLinkGarminId = async () => {
    try {
      const result = await linkGarminFromRecentPush().unwrap();
      await refetchGarmin();

      if (result?.linked) {
        toast.success(`Garmin ID linked${result.backfilled ? ` and ${result.backfilled} activities backfilled` : ''}.`);
        return;
      }

      if (result?.reason === 'multiple-candidates') {
        toast.error('Multiple Garmin IDs were detected in recent syncs, so LeagueRunner will not guess. Please contact support.');
        return;
      }

      toast.info(result?.message || 'Garmin ID is still waiting on a recent sync from Garmin.');
    } catch (e) {
      console.error('Failed to link Garmin ID', e);
      toast.error(e?.data?.message || 'Failed to link Garmin ID');
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    // Build payload without password unless user chose to change it
    const payload = {
      _id: userInfo._id,
      username,
      email,
    };

    if (showPasswordSection && password) {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      payload.password = password;
    }

    try {
      // Update user account details
      const res = await updateProfile(payload).unwrap();
      // Fix: set credentials expects { userInfo }
      dispatch(setCredentials({ userInfo: res }));

      // Update athlete name if available
      if (athlete?._id) {
        try {
          await updateAthlete({ id: athlete._id, firstName, lastName }).unwrap();
        } catch (athleteErr) {
          toast.error(athleteErr?.data?.message || athleteErr.error || 'Failed to update athlete name');
          // continue; user details are already updated
        }
      }

      toast.success('Profile updated successfully');
      // If password was changed, clear and hide section
      if (payload.password) {
        setPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      }
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const startCheckout = async (planType) => {
    try {
      const result = await createBillingCheckoutSession(planType).unwrap();
      window.location.assign(result.url);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to start Stripe checkout');
    }
  };

  const openBillingPortal = async () => {
    try {
      const result = await createBillingPortalSession().unwrap();
      window.location.assign(result.url);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to open billing portal');
    }
  };

  const billingStatusLabel = userInfo?.hasActiveSubscription
    ? `Subscription ${userInfo.billingStatus || 'active'}${userInfo.billingInterval ? ` (${userInfo.billingInterval})` : ''}`
    : userInfo?.hasLifetimeAdFree
      ? 'Lifetime ad-free unlocked'
      : userInfo?.adFree
        ? 'Ad-free enabled by admin'
        : 'Ads enabled';
  const billingPeriodEndLabel = userInfo?.billingCurrentPeriodEnd
    ? new Date(userInfo.billingCurrentPeriodEnd).toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="fade-in">
              <h1 className="text-primary mb-4 text-center">Profile Settings</h1>

              {/* Affiliations Card */}
              {(userLeague || userTeam) && (
                <div className="modern-card mb-4">
                  <div className="modern-card-header">
                    <h3 className="modern-card-title">Your Affiliations</h3>
                    <p className="modern-card-subtitle">
                      Current league and team memberships
                    </p>
                  </div>
                  <div className="grid-modern grid-cols-1 gap-3">
                    {userLeague && (
                      <div
                        className="d-flex align-items-center justify-content-between p-3 rounded"
                        style={{ backgroundColor: 'var(--bg-secondary)' }}
                      >
                        <div>
                          <span className="text-secondary small">League</span>
                          <div className="fw-medium">{userLeague.name}</div>
                        </div>
                        <Link
                          to={`/leagues/${userLeague._id}`}
                          className="btn btn-outline-modern btn-sm-modern"
                        >
                          View League
                        </Link>
                      </div>
                    )}
                    {userTeam && (
                      <div
                        className="d-flex align-items-center justify-content-between p-3 rounded"
                        style={{ backgroundColor: 'var(--bg-secondary)' }}
                      >
                        <div>
                          <span className="text-secondary small">Team</span>
                          <div className="fw-medium">{userTeam.name}</div>
                        </div>
                        <Link
                          to={`/teams/${userTeam._id}`}
                          className="btn btn-outline-modern btn-sm-modern"
                        >
                          View Team
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Profile Update Form */}
              <div className="form-modern">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Update Profile</h3>
                  <p className="modern-card-subtitle">
                    Modify your account information
                  </p>
                </div>

                <Form onSubmit={submitHandler}>
                  <div className="grid-modern grid-cols-2">
                    <div className="form-group-modern">
                      <label
                        htmlFor="firstname"
                        className="form-label-modern"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstname"
                        className="form-input-modern"
                        placeholder="Enter first name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={isLoadingAthlete}
                      />
                    </div>

                    <div className="form-group-modern">
                      <label
                        htmlFor="lastname"
                        className="form-label-modern"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastname"
                        className="form-input-modern"
                        placeholder="Enter last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={isLoadingAthlete}
                      />
                    </div>
                  </div>
                  <div className="form-group-modern">
                    <label
                      htmlFor="username"
                      className="form-label-modern"
                    >
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      className="form-input-modern"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group-modern">
                    <label
                      htmlFor="email"
                      className="form-label-modern"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-input-modern"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {!showPasswordSection && (
                    <button
                      type="button"
                      className="btn btn-outline-modern w-100 mb-3"
                      onClick={() => setShowPasswordSection(true)}
                    >
                      Change Password
                    </button>
                  )}

                  {showPasswordSection && (
                    <div className="password-change-wrapper mb-3">
                      <div className="grid-modern grid-cols-2">
                        <div className="form-group-modern">
                          <label htmlFor="password" className="form-label-modern">
                            New Password
                          </label>
                          <input
                            type="password"
                            id="password"
                            className="form-input-modern"
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                        </div>
                        <div className="form-group-modern">
                          <label htmlFor="confirmPassword" className="form-label-modern">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            id="confirmPassword"
                            className="form-input-modern"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-secondary-modern flex-grow-1"
                          onClick={() => {
                            setShowPasswordSection(false);
                            setPassword('');
                            setConfirmPassword('');
                          }}
                        >
                          Cancel Password Change
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || isUpdatingAthlete}
                    className="btn btn-primary-modern w-100"
                  >
                    {isLoading || isUpdatingAthlete ? 'Updating...' : 'Update Profile'}
                  </button>
                </Form>

                {(isLoading || isLoadingAthlete || isUpdatingAthlete) && <Loader />}
              </div>

              <div className="modern-card mt-4">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Billing</h3>
                  <p className="modern-card-subtitle">Manage ad-free purchases, renewals, and cancellations</p>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap p-3 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <div>
                    <div className="fw-medium">Status: {billingStatusLabel}</div>
                    {userInfo?.hasActiveSubscription && userInfo?.billingStatus && (
                      <div className="text-secondary small mt-1">Stripe subscription status: {userInfo.billingStatus}</div>
                    )}
                    {userInfo?.hasActiveSubscription && billingPeriodEndLabel && (
                      <div className="text-secondary small mt-1">Current access period ends: {billingPeriodEndLabel}</div>
                    )}
                    {userInfo?.hasLifetimeAdFree && (
                      <div className="text-secondary small mt-1">This account has permanent ad-free access.</div>
                    )}
                    <div className="text-secondary small mt-2">
                      Need help with billing, refunds, or a failed renewal? Email <a href="mailto:ethan@leaguerunner.net">ethan@leaguerunner.net</a>.
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-modern btn-sm-modern"
                      onClick={() => startCheckout('lifetime')}
                      disabled={isCreatingCheckout || userInfo?.hasLifetimeAdFree}
                    >
                      {isCreatingCheckout ? 'Redirecting...' : (userInfo?.hasLifetimeAdFree ? 'Lifetime Active' : 'Buy Lifetime Ad-Free')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary-modern btn-sm-modern"
                      onClick={() => startCheckout('subscription')}
                      disabled={isCreatingCheckout || userInfo?.hasActiveSubscription}
                    >
                      {isCreatingCheckout ? 'Redirecting...' : (userInfo?.hasActiveSubscription ? 'Subscription Active' : 'Start Ad-Free Subscription')}
                    </button>
                    {userInfo?.hasBillingCustomer && (
                      <button
                        type="button"
                        className="btn btn-outline-modern btn-sm-modern"
                        onClick={openBillingPortal}
                        disabled={isCreatingPortal}
                      >
                        {isCreatingPortal ? 'Opening...' : 'Manage Billing'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-3 pt-0 text-secondary small">
                  Subscriptions are managed in Stripe&apos;s customer portal. Cancel there any time to stop future renewals. Lifetime purchases are one-time charges.
                </div>
              </div>

              {/* Garmin Integration */}
              <div className="modern-card mt-4">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Strava Integration</h3>
                  <p className="modern-card-subtitle">Connect your Strava account to import activities</p>
                </div>
                <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <div>
                    <div className="fw-medium">Status: {userInfo?.hasStravaConnection ? 'Connected' : 'Not connected'}</div>
                    <div className="text-secondary small mt-1">
                      Disconnecting stops future imports from Strava. Previously imported activities stay in LeagueRunner until you remove them separately.
                    </div>
                  </div>
                  {userInfo?.hasStravaConnection ? (
                    <button
                      type="button"
                      className="btn btn-outline-modern btn-sm-modern"
                      onClick={handleDisconnectStrava}
                      disabled={isDisconnectingStrava}
                    >
                      {isDisconnectingStrava ? 'Disconnecting...' : 'Disconnect Strava'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary-modern btn-sm-modern"
                      onClick={handleConnectStrava}
                    >
                      Connect Strava
                    </button>
                  )}
                </div>
              </div>

              <div className="modern-card mt-4">
                <div className="modern-card-header">
                  <h3 className="modern-card-title">Garmin Integration</h3>
                  <p className="modern-card-subtitle">Connect your Garmin account to import activities</p>
                </div>
                <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <div>
                    <div className="fw-medium">Status: {garminStatus?.connected ? 'Connected' : 'Not connected'}</div>
                    {!!garminStatus?.garminUserId && (
                      <div className="text-secondary small">Garmin User: {garminStatus.garminUserId}</div>
                    )}
                    {!!garminStatus?.awaitingLink && (
                      <div className="text-secondary small mt-1">
                        Garmin is connected, but LeagueRunner still needs Garmin&apos;s account ID before imports can start.
                      </div>
                    )}
                    {!!garminStatus?.manualLinkRequired && (
                      <div className="text-secondary small mt-2">
                        If you already synced a new Garmin activity after reconnecting, click below to link that Garmin ID explicitly. LeagueRunner will not guess automatically.
                      </div>
                    )}
                    {!!garminStatus?.permissions && (
                      <div className="text-secondary small mt-1">
                        Permissions: {Array.isArray(garminStatus.permissions) ? garminStatus.permissions.join(', ') : (typeof garminStatus.permissions === 'object' ? Object.keys(garminStatus.permissions).filter(k => garminStatus.permissions[k]).join(', ') : String(garminStatus.permissions))}
                      </div>
                    )}
                  </div>
                  {garminStatus?.connected ? (
                    <div className="d-flex gap-2">
                      {garminStatus?.manualLinkRequired && (
                        <button className="btn btn-primary-modern btn-sm-modern" onClick={handleLinkGarminId} disabled={isLinkingGarmin}>
                          {isLinkingGarmin ? 'Linking...' : 'Link Garmin ID'}
                        </button>
                      )}
                      <button className="btn btn-outline-modern btn-sm-modern" onClick={handleDisconnectGarmin} disabled={isDisconnecting}>
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect Garmin'}
                      </button>
                    </div>
                  ) : (
                    <button className="btn btn-primary-modern btn-sm-modern" onClick={openGarminConnect}>
                      Connect Garmin
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
